import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {
  Box,
  Button,
  CircularProgress,
  List,
  Paper,
  Stack,
} from '@mui/material';
import React, { useState } from 'react';
import { QuestionFileModel } from 'survey-core';
import { downloadEtiFile } from '../api';

interface FileProps {
  question: QuestionFileModel;
}

export const ETIFilePreview: React.FC<FileProps> = ({ question }) => {
  const [opening, setOpening] = useState<{ [key: string]: boolean }>({});
  const files = question.value || [];

  // Get policyId and taskId from the survey model (set in ETIAnswer)
  const survey = question.survey;
  const policyId = (survey as any)?.policyId;
  const taskId = (survey as any)?.taskId;

  const handleOpenFile = async (fileValue: any) => {
    try {
      setOpening(prev => ({ ...prev, [fileValue.content]: true }));

      // Get the file UUID from fileValue
      const fileUuid = fileValue.uuid || fileValue.content;

      if (!policyId || !taskId) {
        console.error('Missing policyId or taskId from survey');
        return;
      }

      // Fetch the signed URL from backend
      const signedUrl = await downloadEtiFile(policyId, taskId, fileUuid);

      // Open in new tab using window.open (bypasses CORS)
      window.open(signedUrl, '_blank');
    } catch (error) {
      console.error('Opening file failed:', error);
      alert('Failed to open file');
    } finally {
      setOpening(prev => ({ ...prev, [fileValue.content]: false }));
    }
  };

  if (!files || files.length === 0) {
    return (
      <Box
        sx={{
          py: 2,
          px: 1,
          textAlign: 'center',
          color: '#999',
          fontStyle: 'italic',
          fontSize: '0.875rem',
        }}
      >
        No files uploaded
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 1.5, mb: 1 }}>
      <List
        disablePadding
        sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}
      >
        {files.map((file: any, idx: number) => (
          <Paper
            key={idx}
            elevation={0}
            sx={{
              p: 1.5,
              border: '1px solid',
              borderColor: '#d0d0d0',
              borderRadius: 1,
              backgroundColor: '#f9f9f9',
              '&:hover': {
                backgroundColor: '#f5f5f5',
              },
            }}
          >
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              justifyContent="space-between"
            >
              {/* File Info */}
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{ flex: 1, minWidth: 0 }}
              >
                <Box
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    fontSize: '0.9rem',
                    fontWeight: 500,
                    color: '#333',
                  }}
                  title={file.name || `File ${idx + 1}`}
                >
                  {file.name || `File ${idx + 1}`}
                </Box>
              </Stack>

              {/* Open File Button */}
              <Button
                onClick={() => handleOpenFile(file)}
                disabled={opening[file.content]}
                endIcon={
                  opening[file.content] ? (
                    <CircularProgress size={16} sx={{ mr: -0.5 }} />
                  ) : (
                    <OpenInNewIcon />
                  )
                }
                sx={{
                  flexShrink: 0,
                  px: 1.5,
                  py: 0.75,
                  textTransform: 'none',
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  backgroundColor: '#19b394',
                  color: '#fff',
                  border: '1px solid #19b394',
                  borderRadius: '4px',
                  whiteSpace: 'nowrap',
                  '&:hover': {
                    backgroundColor: '#14a48b',
                    borderColor: '#14a48b',
                  },
                  '&:disabled': {
                    backgroundColor: '#ccc',
                    borderColor: '#ccc',
                    color: '#fff',
                  },
                }}
              >
                {opening[file.content] ? 'Opening...' : 'Open'}
              </Button>
            </Stack>
          </Paper>
        ))}
      </List>
    </Box>
  );
};
